import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import { axiosBaseQuery } from '@consolidocs-ui/utils';
import { getApiRuntime } from './runtimeConfig';
import {
  TOKEN_EXPIRED_EVENT,
  clearStoredSession,
  isSessionTokenExpired,
} from './cognitoToken';

/**
 * Historical value — persisted Redux state and both apps' rootReducers key off
 * this exact string. The name is wrong (main is not super-admin) but renaming
 * it is a separate, state-breaking change. See CLAUDE.md.
 */
export const API_REDUCER_PATH = 'consolidate-docs-super-admin';

/**
 * Whether a failed response means the session itself is gone.
 *
 * 401 is unambiguous. 403 is not: the backend returns it both for an
 * unauthenticated request and for a signed-in user who simply lacks
 * permission, and the app has plenty of the latter (assigning users, renaming
 * a folder, department admin endpoints). Treating every 403 as expiry tore
 * down the app mid-session and stranded people on the "Session Expired"
 * screen, so the token decides.
 */
const isSessionGone = (status: number | undefined, token: string): boolean =>
  status === 401 || (status === 403 && isSessionTokenExpired(token));

const makeBaseQuery =
  (key: 'default' | 'ai'): BaseQueryFn =>
  async (args, api, extraOptions) => {
    // Resolved per request, not at module load, so import order stays safe.
    const { baseUrl, getToken } = getApiRuntime(key);
    const token = getToken();
    const result = await axiosBaseQuery(baseUrl, token)(
      args,
      api,
      extraOptions
    );

    if (result.error) {
      const status = (result.error as { status?: number })?.status;

      if (isSessionGone(status, token)) {
        clearStoredSession();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(TOKEN_EXPIRED_EVENT, { detail: { status } })
          );
        }
      }
    }

    return result;
  };

export const baseApi = createApi({
  reducerPath: API_REDUCER_PATH,
  baseQuery: makeBaseQuery('default'),
  endpoints: () => ({}),
});

export const enhanceEndpoints = baseApi.enhanceEndpoints;

export const aiBaseQuery = makeBaseQuery('ai');
